import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { GridCellProps, MultiGrid } from 'react-virtualized';
import { IPreviewDataHeader, IPreviewDataRows } from '../utils';
import { Order } from '../../../../utils/apollo/graphqlSchema';
import useSortablePreviewDataRows from '../DataModelerShared/useSortablePreviewDataRows';
import { GridCell, HeaderGridCell } from '../DataModelerCommonUI';
import SortableHeaderLabel from '../../../pageForm/SortableHeaderLabel';
import LongNameWithTooltip from '../LongNameWithTooltip';
import { freeze } from '@reduxjs/toolkit';
import { exportTableToCsv } from '../../../../components/MultiGridTable';
import { CellContentWrapper } from './DataModelerPreviewDataGridUI';

interface IProps {
  previewCachedData: IPreviewDataRows | null;
  lastColumnIsResult: boolean;
}

const emptyPreviewData: IPreviewDataRows = freeze({
  header: [],
  rows: [],
  sqlQuery: null,
  errors: [],
});

export const ROW_HEIGHT = 32;
const MIN_CELL_WIDTH = 136;
const MAX_CELL_WIDTH = 400;
const INITIAL_GRID_WIDTH = 1024;

const calcCellWidth = (strLen: number) =>
  Math.max(MIN_CELL_WIDTH, Math.min(MAX_CELL_WIDTH, strLen * 6));

const useGridHeaderRenderer = ({
  headers,
  onHeaderClick,
  sortColumnIndex,
  sortOrder,
}: {
  headers: IPreviewDataHeader[];
  onHeaderClick: (colIndex: number) => void;
  sortColumnIndex: number;
  sortOrder: Order;
}) => {
  return useCallback(
    ({
      columnIndex,
      key,
      style,
    }: {
      columnIndex: number;
      rowIndex: number;
      key: string;
      style: CSSProperties;
    }) => {
      const header = headers[columnIndex];
      return (
        <HeaderGridCell
          key={`${key}-hdr-${columnIndex}`}
          style={style}
          $isHeader={true}
          $borders={true}
        >
          <CellContentWrapper>
            <SortableHeaderLabel
              text={<LongNameWithTooltip name={header.name} />}
              onClick={() => onHeaderClick(columnIndex)}
              activeSortType={sortOrder}
              isActive={sortColumnIndex === columnIndex}
              shouldAlignRight={false}
            />
          </CellContentWrapper>
        </HeaderGridCell>
      );
    },
    [headers, onHeaderClick, sortColumnIndex, sortOrder],
  );
};

export const usePreviewDataGridCellRenderer = ({
  previewCachedData,
  lastColumnIsResult,
}: IProps) => {
  const previewData = previewCachedData ?? emptyPreviewData;
  const { sortedRows, onHeaderClick, sortColumnIndex, sortOrder } =
    useSortablePreviewDataRows({ previewData });
  const numCols = previewCachedData?.header.length ?? 0;

  const exportToCsv = useCallback(() => {
    exportTableToCsv({
      display: `Preview Values`,
      headers: previewData.header.map((hdr) => hdr.name),
      data: sortedRows.map((row) => row.values),
    });
  }, [previewData.header, sortedRows]);

  const headerRenderer = useGridHeaderRenderer({
    headers: previewData.header,
    onHeaderClick,
    sortColumnIndex,
    sortOrder,
  });

  const cellRenderer = useCallback(
    ({ columnIndex, key, rowIndex, style }: GridCellProps) => {
      if (rowIndex === 0) {
        return headerRenderer({ columnIndex, key, rowIndex, style });
      }
      const listIndex = rowIndex - 1;
      const row = sortedRows[listIndex];
      if (!row) {
        return null;
      }
      const columnValue = row.values[columnIndex];
      return (
        <GridCell
          key={`${key}-value-${rowIndex}-${columnIndex}`}
          style={style}
          $isEven={rowIndex % 2 === 0}
          $borders={true}
        >
          <CellContentWrapper
            $isResultColumn={lastColumnIsResult && columnIndex === numCols - 1}
          >
            <LongNameWithTooltip name={columnValue} />
          </CellContentWrapper>
        </GridCell>
      );
    },
    [sortedRows, headerRenderer, lastColumnIsResult, numCols],
  );

  return {
    cellRenderer,
    rowCount: previewData.rows.length,
    exportToCsv,
  };
};

const calcMinColumnWidths = (
  previewData: IPreviewDataRows | null,
): number[] => {
  const ret = Array(previewData?.header.length ?? 0).map((_) => 0);
  previewData?.header.forEach((hdr, colIx) => {
    ret[colIx] = calcCellWidth(hdr.name.length) + 20; // + 20 for sort icon
    previewData.rows.forEach((row) => {
      ret[colIx] = Math.max(
        ret[colIx],
        calcCellWidth(row.values[colIx].length),
      );
    });
  });
  return ret;
};

const SCROLLBAR_WIDTH = 6;

const calcColumWidths = (
  minColumnWidths: number[],
  gridViewWidth: number,
  hasVerticalScrollbar: boolean,
): number[] => {
  if (minColumnWidths.length === 0) {
    return [];
  }
  const sumOfMinColumnWidths = minColumnWidths.reduce(
    (prev, cur) => prev + cur,
    0,
  );
  if (sumOfMinColumnWidths >= gridViewWidth) {
    return [...minColumnWidths];
  }
  const toDistribute =
    gridViewWidth -
    (hasVerticalScrollbar ? SCROLLBAR_WIDTH : 0) -
    sumOfMinColumnWidths;
  const toDistributePerColumn = Math.floor(
    toDistribute / minColumnWidths.length,
  );
  const toDistributePerLastColumn =
    toDistributePerColumn + (toDistribute % minColumnWidths.length);
  return minColumnWidths.map((n, ix) =>
    ix === minColumnWidths.length - 1
      ? n + toDistributePerLastColumn
      : n + toDistributePerColumn,
  );
};

export const useCdwTableGridColumnWidths = ({
  previewData,
  hasVerticalScrollbar,
}: {
  previewData: IPreviewDataRows | null;
  hasVerticalScrollbar: boolean;
}) => {
  const multiGridRef = useRef<MultiGrid>(null);

  const current = multiGridRef.current;
  const initialWidth = current?.props.width ?? INITIAL_GRID_WIDTH;
  const [columnWidths, setColumnWidths] = useState(
    calcColumWidths(
      calcMinColumnWidths(previewData),
      initialWidth,
      hasVerticalScrollbar,
    ),
  );

  const getColumnWidth = useCallback(
    ({ index }): number => {
      return columnWidths[index] ?? MIN_CELL_WIDTH;
    },
    [columnWidths],
  );

  const onResize = useCallback(() => {
    if (multiGridRef.current?.props.width) {
      const newWidths = calcColumWidths(
        calcMinColumnWidths(previewData),
        multiGridRef.current?.props.width,
        hasVerticalScrollbar,
      );
      setColumnWidths(newWidths);
    }
  }, [multiGridRef, previewData, hasVerticalScrollbar]);

  // Trigger re-compute whenever our widths change
  useEffect(() => {
    current?.recomputeGridSize();
  }, [columnWidths, current]);

  return {
    multiGridRef,
    getColumnWidth,
    onResize,
  };
};
